import { MetaRepository } from '../../repositories/MetaRepository'

/**
 * pull-backoff.js - contador de fallos + ventana de reintento POR CATALOGO del pull Tier B
 * (TKT-2240). Vive aparte del pull-engine para poder testearlo aislado y para que el engine
 * siga leyendose como "SWR + cursores".
 *
 * PROBLEMA: pullCatalog SOLO escribe el cursor al final y SOLO si todo salio bien. Si una
 * pagina falla (timeout, 5xx, ruta inexistente) la excepcion se traga con dbgWarn, el cursor NO
 * avanza, isStale() sigue true y el catalogo COMPLETO se re-descarga desde la pagina 1 en el
 * siguiente tick de 30 s, para siempre y sin backoff. Con `users` (~6k filas, ~30 requests) eso
 * son cientos de MB por hora POR DEVICE. La misma patologia (un endpoint muerto martillado cada
 * 30 s sin backoff) tumbo la flota el 14-ago-2026: ver el comentario historico en sync-worker.js.
 *
 * MODELO: una marca `cursor:fail:<catalogo>` en la tabla meta, al lado de los cursores
 * (`cursor:<catalogo>`), con { fails, next_attempt_at, reason }. revalidateStale SALTA el
 * catalogo mientras su ventana no venza; un exito real (cursor escrito) BORRA la marca.
 *
 * Nota de alcance: el gate se aplica en revalidateStale (el carril automatico cada 30 s), NO en
 * pullCatalog. Una llamada EXPLICITA (bootstrapTierB tras login, prefetch de un form que el
 * usuario abrio, la superficie de QA) debe poder intentarlo siempre: el backoff existe para el
 * bucle desatendido, no para bloquear al usuario.
 */

// Escalera de espera tras fallo (ms): 1 min, 5 min, 30 min, 2 h, tope 6 h.
export const PULL_BACKOFF_STEPS_MS = [60000, 300000, 1800000, 7200000, 21600000]

// Ventana maxima que el diseño admite. Se deriva de la escalera para que no puedan divergir.
const PULL_BACKOFF_TOPE_MS = Math.max(...PULL_BACKOFF_STEPS_MS)

// Ventana del EMPTY-RESPONSE GUARD: un 200 vacio sobre un catalogo poblado tampoco toca el
// cursor, asi que sin esto se re-pullea cada tick (payload chico, requests infinitos). Entra a la
// MISMA escalera (empieza en 1 min y escala si el server sigue devolviendo vacio).
export const PULL_EMPTY_GUARD_REASON = 'empty-response'

// 4xx PERMANENTE: una ruta que no existe no se arregla reintentando (precedente del 14-ago). Salta
// directo al ultimo escalon en vez de recorrer la escalera.
const PERMANENT_STATUSES = new Set([404, 405, 410])

const failKey = (name) => 'cursor:fail:' + name

/** Lectura dual-shape del status (igual que classify.js: el interceptor rechaza con el response). */
function readStatus (error) {
  if (error == null) return null
  return error.status ?? error.response?.status ?? null
}

/**
 * Fraccion uniforme en [0, 1). Usa crypto.getRandomValues en vez de Math.random: para dispersar
 * reintentos cualquiera de las dos sirve, pero la segunda queda marcada como hotspot de seguridad
 * y no vale la pena discutirla en cada revision. Existe en el WebView de Android/iOS y en Node.
 */
function unitRandom () {
  const buf = new Uint32Array(1)
  globalThis.crypto.getRandomValues(buf)
  return buf[0] / 4294967296
}

/**
 * Jitter de mas/menos 20% (mismo criterio que classify.backoffFor): sin el, todo el fleet
 * reintentaria en lock-step tras una caida del backend y lo estampidaria al recuperarse.
 */
function withJitter (baseMs) {
  return Math.round(baseMs + baseMs * 0.2 * (unitRandom() * 2 - 1))
}

/** Escalon de la escalera para el n-esimo fallo (1-based), saturado al tope. */
export function pullBackoffFor (fails) {
  const idx = Math.min(Math.max(fails, 1) - 1, PULL_BACKOFF_STEPS_MS.length - 1)
  return PULL_BACKOFF_STEPS_MS[idx]
}

/** Marca de fallo del catalogo, o null si no hay (o si esta corrupta). */
export async function readPullFailure (name) {
  const raw = await MetaRepository.get(failKey(name))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/**
 * True si el catalogo esta dentro de su ventana de backoff (el carril automatico debe saltarlo).
 * Tolerante a fallos: si la lectura revienta, devuelve false (mejor un pull de mas que un
 * catalogo congelado por un error de storage).
 */
export async function isPullBackedOff (name, now = Date.now()) {
  try {
    const rec = await readPullFailure(name)
    if (!rec || typeof rec.next_attempt_at !== 'number') return false
    const restante = rec.next_attempt_at - now
    // Cota de cordura: `next_attempt_at` es un epoch ABSOLUTO, así que si el reloj del dispositivo
    // salta hacia atrás (ajuste de hora, RTC malo tras un reboot — común en la gama baja de la
    // flota) una ventana de 1 min se convierte en tantas horas como haya saltado el reloj, y el
    // catálogo deja de refrescarse hasta que el reloj lo alcance. Ninguna ventana legítima puede
    // pasar del tope de la escalera; una mayor es basura y se trata como vencida.
    return restante > 0 && restante <= PULL_BACKOFF_TOPE_MS * 1.25
  } catch {
    return false
  }
}

/**
 * Registra un fallo del catalogo y abre su ventana de espera. Best-effort: NUNCA lanza (el
 * caller ya esta en su camino de error y no debe romperse por no poder escribir la marca).
 *
 * @param {string} name
 * @param {{ error?: any, reason?: string, now?: number }} opts
 * @returns {Promise<{ fails: number, next_attempt_at: number, reason: string }|null>}
 */
export async function recordPullFailure (name, { error, reason, now = Date.now() } = {}) {
  try {
    const prev = await readPullFailure(name)
    const fails = (prev && Number(prev.fails)) > 0 ? Number(prev.fails) + 1 : 1
    const status = readStatus(error)
    const permanent = status != null && PERMANENT_STATUSES.has(status)
    // Permanente: al tope de la escalera desde el primer golpe (circuit breaker de facto).
    const base = permanent ? PULL_BACKOFF_STEPS_MS[PULL_BACKOFF_STEPS_MS.length - 1] : pullBackoffFor(fails)
    const record = {
      fails,
      next_attempt_at: now + withJitter(base),
      reason: reason || (status != null ? 'http-' + status : 'error')
    }
    await MetaRepository.put(failKey(name), JSON.stringify(record))
    return record
  } catch {
    return null
  }
}

/**
 * Borra la marca de fallo tras un exito REAL (cursor escrito). Best-effort: nunca lanza.
 */
export async function clearPullFailure (name) {
  try {
    await MetaRepository.del(failKey(name))
  } catch { /* best-effort */ }
}
